import { useCallback, useEffect, useRef, useState } from 'react';
import { shopPaymentAPI } from '../../../services/api';
import { messageFromError } from '../parts/errorMessage';

export const POLL_MS = 10000;
export const POLL_LIMIT_MS = 120000;

// 403 carries {message, upgrade_required, upgrade_url} rather than a string.
const connectMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && typeof detail.message === 'string') {
    return detail.message;
  }
  return messageFromError(err, 'Could not reach Stripe. Try again in a minute.');
};

// The shop's Stripe account. A 404 means the feature flag is off (`enabled`
// false: the section renders nothing). After Stripe sends the mechanic back
// (?payments=return) we refetch every 10 s for up to 2 minutes while the
// account is not yet ready, since Stripe takes ~90 s to enable charges.
export const useShopPayments = () => {
  const [account, setAccount] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [checking, setChecking] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const fetchAccount = useCallback(async () => {
    try {
      const data = await shopPaymentAPI.getAccount();
      if (!mounted.current) return null;
      setAccount(data);
      setError('');
      return data;
    } catch (err) {
      if (!mounted.current) return null;
      if (err?.response?.status === 404) setEnabled(false);
      else setError(messageFromError(err, 'Could not load your card payment settings.'));
      return null;
    }
  }, []);

  useEffect(() => {
    let timer = null;
    let cancelled = false;
    const returning = new URLSearchParams(window.location.search).get('payments') === 'return';

    (async () => {
      const first = await fetchAccount();
      if (!mounted.current || cancelled) return;
      setLoading(false);
      if (!returning || !first || first.state === 'ready') return;

      setChecking(true);
      const started = Date.now();
      let failures = 0;
      timer = setInterval(async () => {
        const data = await fetchAccount();
        failures = data ? 0 : failures + 1;
        const done = failures >= 2 || (data && data.state === 'ready')
          || Date.now() - started >= POLL_LIMIT_MS;
        if (done) {
          clearInterval(timer);
          if (mounted.current) setChecking(false);
        }
      }, POLL_MS);
    })();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [fetchAccount]);

  // country ('US' | 'CA') fixes a NEW Stripe account's country; the server
  // ignores it once the shop has an account.
  const connect = async (country) => {
    setError('');
    setConnecting(true);
    try {
      const data = await shopPaymentAPI.connect(country);
      window.location.href = data.url;
      // Leave the button disabled: the page is navigating away.
    } catch (err) {
      if (mounted.current) {
        setError(connectMessage(err));
        setConnecting(false);
      }
    }
  };

  return { account, enabled, loading, error, checking, connecting, connect };
};

export default useShopPayments;
